/* eslint-disable @typescript-eslint/no-unused-vars */
import { Component, Inject, OnInit, Optional } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Router } from '@angular/router';
import {
  DialogRef,
  DIALOG_DATA,
} from '@cyborg-monorepo/common/angular/cyborg-ui';
import {
  CollectionRecord,
  Enums,
  Environment,
  ExpandedHuntPackageRecord,
  ExpandedProfile,
  Hunt,
  HuntTemplate,
  Profile,
} from '@cyborg-monorepo/hunter/common';
import { forkJoin, map, Observable, tap } from 'rxjs';
import { isHuntPackageHuntCompatible } from '../../../../../helpers/utils';
import { CompanyService } from '../../../services/company.service';
import { ContentService } from '../../../services/content.service';
import { EnvironmentService } from '../../../services/environment.service';
import { HuntService } from '../../../services/hunt.service';
import { UserService } from '../../../services/user.service';
import { CommonModule } from '@angular/common';
import { ButtonComponent, ErrorComponent, InputComponent, MultiSelectComponent, SelectComponent } from '@hyperkit';
import { CyborgNotificationComponent } from 'libs/common/angular/cyborg-ui/src/lib/components/cyborg-notification/cyborg-notification.component';

// Interface representing compatibility summary for environments and tools
export interface CompatSummary {
  envName: string;
  envId: string;
  tools: {
    toolId: string;
    toolName: string;
    missingCategories: string[];
    missingFields?: {
      category: string;
      fields: string[];
    }[];
  }[];
}

// Interface representing missing fields for tools
export interface MissingFieldsSummary {
  toolId: string;
  toolName: string;
  missingCategories: string[];
}

// Component decorator defining metadata
@Component({ standalone: true,
  imports: [
    CommonModule,
    ErrorComponent,
    MultiSelectComponent,
    ReactiveFormsModule,
    SelectComponent,
    ButtonComponent,
    InputComponent,
    CyborgNotificationComponent

  ],  
  selector: 'new-hunt-dialog',
  templateUrl: './new-hunt-dialog.component.html',
  styleUrls: ['./new-hunt-dialog.component.scss'],
})
export class NewHuntDialogComponent implements OnInit {
  // Form group to manage the form controls
  public formGroup: FormGroup;
  public priorities: Array<string> = [];
  public assignees$: Observable<Array<Profile>>; // Observable for dropdown users
  public selectedObject: Profile; // Selected profile object
  public generatedHuntPackages: ExpandedHuntPackageRecord[];
  public placeholderName: string = ''; // Default placeholder name
  public defaultAssignee: Profile & ExpandedProfile; // Default assignee
  public user: Profile; // Current user profile
  public error: string; // Error message for UI
  public environments: Environment[]; // List of environments
  public warnings: CompatSummary[] = []; // Compatibility warnings
  public checkWarnings: boolean = false; // Flag for warnings display

  constructor(
    public dialogRef: DialogRef,
    private formBuilder: FormBuilder,
    private companyService: CompanyService,
    private huntService: HuntService,
    private contentService: ContentService,
    private userService: UserService,
    private environmentService: EnvironmentService,
    private router: Router,
    @Optional()
    @Inject(DIALOG_DATA)
    public data: {
      huntPackages: Array<ExpandedHuntPackageRecord>; // Data for hunt packages
      huntTemplate: HuntTemplate; // Data for hunt templates
      collection: CollectionRecord; // Data for collections
    },
  ) {}

  // Lifecycle method called on component initialization
  public ngOnInit(): void {
    this.formGroup = this.formBuilder.group({
      name: [
        this.truncateString(this.placeholderName, 50),
        Validators.required,
      ],
      environments: new FormControl(
        Array.isArray(this.data.huntTemplate?.environments)
          ? this.data.huntTemplate.environments
          : [],
        [Validators.required],
      ),
      assignee: [this.defaultAssignee ? this.defaultAssignee : null],
      priority: [Enums.PriorityEnum.LOW, Validators.required],
    });

    const environments = Array.isArray(this.formGroup.value.environments)
      ? this.formGroup.value.environments
      : [];
    if (environments.length > 0) {
      this.generateContentForEnv();
    } else {
      this.warnings = [];
    }

    this.userService.getProfile().subscribe((user) => {
      this.defaultAssignee = {
        ...user,
        fullName: user.firstName + ' ' + user.lastName,
      };

      this.environmentService.getAllEnvironments().subscribe((environments) => {
        this.environments = environments;
      });

      if (this.data.huntTemplate?.defaultAssignee) {
        this.defaultAssignee = {
          ...this.data.huntTemplate.defaultAssignee,
          fullName:
            this.data.huntTemplate.defaultAssignee.firstName +
            ' ' +
            this.data.huntTemplate.defaultAssignee.lastName,
        };
      }

      this.formGroup.patchValue({
        assignee: this.defaultAssignee ? this.defaultAssignee : null,
      });
    });

    this.assignees$ = this.companyService.getUsers().pipe(
      map((users: Array<Profile>) =>
        users.map((user) => ({
          ...user,
          fullName: user.firstName + ' ' + user.lastName,
        })),
      ),
    );

    if (this.data.collection) {
      const date: string = new Date(Date.now()).toLocaleDateString();
      this.placeholderName =
        this.truncateString(this.data.collection.title, 50 - date.length - 1) +
        ' ' +
        date;
    } else if (this.data.huntPackages.length === 1) {
      this.placeholderName = this.truncateString(
        this.data.huntPackages[0].title,
        50,
      );
    }

    if (this.data.huntTemplate) {
      const date: string = new Date(Date.now()).toLocaleDateString();
      this.placeholderName =
        this.truncateString(this.data.huntTemplate.name, 50 - date.length - 1) +
        ' ' +
        date;
    }

    this.formGroup.patchValue({
      name: this.placeholderName || null,
    });

    this.priorities = Object.values(Enums.PriorityEnum);
  }

  // Handles form submission
  public onSubmit(): void {
    const hunt: Hunt = this.formGroup.value; // Get form values
    hunt.status = Enums.StatusEnum.TO_DO; // Set default status

    hunt.environments = this.environments.filter((env) => {
      return this.formGroup.value.environments.includes(env.id);
    });

    // Assign hunt template or generated hunt packages
    if (this.data.huntTemplate) {
      hunt.huntTemplate = this.data.huntTemplate;
      hunt.huntPackages = this.data.huntTemplate.huntPackages;
    } else {
      hunt.huntPackages = this.generatedHuntPackages;
      if (this.data.collection) {
        hunt.hypothesis = this.data.collection.synopsis;
        hunt.abstract = this.data.collection.threat_summary;
      } else {
        if (this.data.huntPackages.length === 1) {
          hunt.abstract = this.data.huntPackages[0].context.threat_description;
          hunt.hypothesis = this.data.huntPackages[0].description;
        }
      }
    }

    // Call hunt service to save the hunt
    this.huntService.createHunt(hunt).subscribe(
      (hunt: Hunt) => {
        this.dialogRef.close(hunt); // Close the dialog on success
      },
      (error) => {
        this.error = error.message; // Show error message on failure
      },
    );
  }

  // Truncate a string to a specified length
  public truncateString(string, limit): string {
    if (string.length > limit) {
      return string.substring(0, limit - 3) + '...';
    } else {
      return string;
    }
  }

  // Remove a warning tag
  public removeTag(event): void {
    const tagIndex = this.warnings.findIndex(
      (warning) => warning.envId == event.dataItem.id,
    );
    this.warnings.splice(tagIndex, 1);
  }

  // Handle value change event in environments dropdown
  public onValueChange(event): void {
    if (event && event.length > 0) {
      this.generateContentForEnv();
    } else {
      this.warnings = [];
    }
  }

  // Generate content for the selected environments
  public generateContentForEnv(): void {
    // Ensure environments is an array and not null/undefined
    const environments = Array.isArray(this.formGroup?.value?.environments)
      ? this.formGroup.value.environments
      : [];

    if (!environments || environments.length === 0) {
      this.warnings = [];
      return;
    }

    this.checkWarnings = false;

    // Validate huntPackages
    const huntPackages = Array.isArray(this.data.huntPackages)
      ? this.data.huntPackages
      : [];

    if (huntPackages.length === 0) {
      this.generatedHuntPackages = [];
      this.checkWarnings = true;
      return;
    }

    // Generate observables for compatible hunt packages
    const generatedHuntPackages: Observable<ExpandedHuntPackageRecord>[] =
      huntPackages
        .filter((huntPackage) => isHuntPackageHuntCompatible(huntPackage))
        .map((huntPackage) => {
          const contentRequest =
            huntPackage.source === Enums.SourceEnum.CUSTOMER
              ? this.contentService.generateCustomContent(huntPackage.UUID, {
                  environments: environments,
                })
              : this.contentService.getHuntPackageContent(huntPackage.UUID, {
                  environments: environments,
                });

          return contentRequest.pipe(
            map((generatedContent: Environment[]) => {
              // Map and filter environment tools and compatibility
              generatedContent = generatedContent.map((env) => {
                env.tools = env.tools.filter((tool) => {
                  return (
                    tool.generatedContent &&
                    tool.generatedContent?.Hunt?.length > 0
                  );
                });
                return env;
              });

              const expanded = huntPackage as ExpandedHuntPackageRecord;
              expanded.analysis = expanded.originalAnalysis = generatedContent;
              return expanded;
            }),
          );
        });

    if (!generatedHuntPackages || generatedHuntPackages.length === 0) {
      this.generatedHuntPackages = [];
      this.checkWarnings = true;
      return;
    }

    // Process the generated observables using forkJoin
    forkJoin(generatedHuntPackages).subscribe({
      next: (generatedHuntPacks) => {
        this.generatedHuntPackages = generatedHuntPacks;
        this.checkWarnings = true;
      },
      error: (err) => {
        this.error = 'Failed to generate content for environments.';
        this.checkWarnings = true;
      },
    });
  }

  // Navigate to the settings page
  public goToSettings(): void {
    this.dialogRef.close();
    this.router.navigate(['/settings/environment/']);
  }

  // Close the dialog
  public onClose(): void {
    this.dialogRef.close();
  }
}
